import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { useLang } from '@/hooks/useLang'
import { Clock, Phone, ChevronDown, Globe, Check } from 'lucide-react'
import { generateSeo } from '@/lib/seo'
import { Breadcrumbs } from '@/components/Breadcrumbs'
import { ToolPageHero } from '@/components/ToolPageHero'
import { FAQSection } from '@/components/FAQSection'
import { CTASection } from '@/components/CTASection'
import { useState, useMemo, useEffect } from 'react'
import { COUNTRIES_CONFIG } from '@/config/countries'
import { Flag } from '@/components/Flag'
import {
  detectUserCountry,
  getCurrentTimeInCountry,
  getTimeDifference,
  getBestCallingHours,
  HourQuality,
} from '@/config/country-timezones'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/$lang/tools/best-time-to-call')({
  staticData: {
    sitemap: { priority: '0.7', changefreq: 'monthly' },
  },
  component: BestTimeToCallToolPage,
  head: ({ match }) => {
    const { meta, links } = generateSeo({
      title: 'Best Time to Call - International Calling Hours | PhoneSalad',
      description: 'Find the best time to call any country. Check current local times, time differences, and optimal calling windows for international calls.',
      pathname: match.pathname,
      keywords: 'best time to call, international calling hours, time zone calculator, call schedule, time difference',
    })
    return { meta, links }
  },
})

// Popular calling routes
const POPULAR_ROUTES = [
  { from: 'US', to: 'IN' },
  { from: 'US', to: 'GB' },
  { from: 'US', to: 'MX' },
  { from: 'CA', to: 'PH' },
  { from: 'AU', to: 'GB' },
  { from: 'GB', to: 'IN' },
]

// Get quality color for the timeline
function getQualityColor(quality: HourQuality): string {
  switch (quality) {
    case 'great':
      return 'bg-green-500'
    case 'okay':
      return 'bg-yellow-500'
    case 'avoid':
      return 'bg-gray-300 dark:bg-gray-600'
  }
}

// Format hour to short string (e.g., "2 PM")
function formatHourShort(hour: number): string {
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${displayHour} ${period}`
}

// Country selector component
interface CountryPickerProps {
  value: string | null
  onChange: (code: string) => void
  label: string
}

function CountryPicker({ value, onChange, label }: CountryPickerProps) {
  const { t } = useTranslation(['tools', 'common'])
  const [open, setOpen] = useState(false)

  const selectedCountry = value ? COUNTRIES_CONFIG[value] : null

  const countries = useMemo(() => {
    return Object.values(COUNTRIES_CONFIG).sort((a, b) =>
      t(`countries.${a.code}`).localeCompare(t(`countries.${b.code}`))
    )
  }, [t])

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-muted-foreground">
        {label}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-auto py-3 px-4"
          >
            {selectedCountry ? (
              <div className="flex items-center gap-3">
                <Flag countryCode={selectedCountry.code} size="lg" />
                <div className="text-left">
                  <div className="font-medium">{t(`countries.${selectedCountry.code}`)}</div>
                  <div className="text-xs text-muted-foreground">
                    {selectedCountry.code}
                  </div>
                </div>
              </div>
            ) : (
              <span className="text-muted-foreground">
                {t('tools.bestTimeToCall.selectCountry')}
              </span>
            )}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('tools.bestTimeToCall.searchCountry')} />
            <CommandList>
              <CommandEmpty>{t('tools.bestTimeToCall.noCountriesFound')}</CommandEmpty>
              <CommandGroup>
                {countries.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={`${t(`countries.${country.code}`)} ${country.code}`}
                    onSelect={() => {
                      onChange(country.code)
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === country.code ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <Flag countryCode={country.code} size="md" className="mr-2" />
                    <span>{t(`countries.${country.code}`)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

function BestTimeToCallToolPage() {
  const { t } = useTranslation(['tools', 'common'])
  useLang()
  const [fromCountry, setFromCountry] = useState<string | null>(null)
  const [toCountry, setToCountry] = useState<string | null>(null)
  const [hoveredHour, setHoveredHour] = useState<number | null>(null)

  // Auto-detect user's country on mount
  useEffect(() => {
    const detected = detectUserCountry()
    if (detected && COUNTRIES_CONFIG[detected]) {
      setFromCountry(detected)
    }
  }, [])

  const fromTime = useMemo(() => {
    if (!fromCountry) return null
    return getCurrentTimeInCountry(fromCountry)
  }, [fromCountry])

  const toTime = useMemo(() => {
    if (!toCountry) return null
    return getCurrentTimeInCountry(toCountry)
  }, [toCountry])

  const timeDiff = useMemo(() => {
    if (!fromCountry || !toCountry) return null
    return getTimeDifference(fromCountry, toCountry)
  }, [fromCountry, toCountry])

  const bestHours = useMemo(() => {
    if (!fromCountry || !toCountry) return null
    return getBestCallingHours(fromCountry, toCountry)
  }, [fromCountry, toCountry])

  const fromConfig = fromCountry ? COUNTRIES_CONFIG[fromCountry] : null
  const toConfig = toCountry ? COUNTRIES_CONFIG[toCountry] : null

  // Helper to format time difference
  const formatTimeDiff = (diff: number): string => {
    const absDiff = Math.abs(diff)
    const hours = Math.floor(absDiff)
    const minutes = Math.round((absDiff - hours) * 60)

    if (hours === 0 && minutes === 0) {
      return t('tools.bestTimeToCall.sameTime')
    }

    let timeStr = ''
    if (hours > 0) {
      timeStr = `${hours}h`
    }
    if (minutes > 0) {
      timeStr += ` ${minutes}m`
    }

    if (diff > 0) {
      return t('tools.bestTimeToCall.hoursAhead', { hours: timeStr.trim() })
    }
    return t('tools.bestTimeToCall.hoursBehind', { hours: timeStr.trim() })
  }

  const handlePopularRoute = (from: string, to: string) => {
    setFromCountry(from)
    setToCountry(to)
    // Scroll to results
    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const faqItems = t('tools.bestTimeToCall.faq.items', { returnObjects: true }) as unknown as Array<{ question: string; answer: string }>

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('tools.breadcrumb'),
            to: '/$lang/tools',
          },
          {
            label: t('tools.bestTimeToCall.title'),
          },
        ]}
      />

      <ToolPageHero
        icon={Clock}
        badgeText={t('tools.bestTimeToCall.title')}
        title={t('tools.bestTimeToCall.title')}
        description={t('tools.bestTimeToCall.description')}
      >
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <CountryPicker
                value={fromCountry}
                onChange={setFromCountry}
                label={t('tools.bestTimeToCall.fromCountry')}
              />
              <CountryPicker
                value={toCountry}
                onChange={setToCountry}
                label={t('tools.bestTimeToCall.toCountry')}
              />
            </div>
          </CardContent>
        </Card>
      </ToolPageHero>

      {/* Results */}
      {fromCountry && toCountry && bestHours && (
        <section id="results" className="pb-8 px-4">
          <div className="container mx-auto max-w-4xl space-y-8">
            {/* Current Times */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* From Country Time */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <Flag countryCode={fromConfig?.code} size="xl" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('tools.bestTimeToCall.fromCountry')}
                      </p>
                      <p className="text-xl font-semibold">{fromCountry && t(`countries.${fromCountry}`)}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      {t('tools.bestTimeToCall.currentTime')}
                    </p>
                    <p className="text-3xl font-bold">
                      {fromTime?.formatted || '--:--'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* To Country Time */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <Flag countryCode={toConfig?.code} size="xl" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('tools.bestTimeToCall.toCountry')}
                      </p>
                      <p className="text-xl font-semibold">{toCountry && t(`countries.${toCountry}`)}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      {t('tools.bestTimeToCall.currentTime')}
                    </p>
                    <p className="text-3xl font-bold">
                      {toTime?.formatted || '--:--'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Time Difference */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  {t('tools.bestTimeToCall.timeDifference')}
                </p>
                <p className="text-2xl font-bold">
                  {timeDiff !== null && formatTimeDiff(timeDiff)}
                </p>
              </CardContent>
            </Card>

            {/* Timeline - Single bar with dual axes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>{t('tools.bestTimeToCall.timeline.title')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Legend */}
                <div className="flex flex-wrap gap-4 mb-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-500"></div>
                    <span>{t('tools.bestTimeToCall.timeline.great')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-500"></div>
                    <span>{t('tools.bestTimeToCall.timeline.okay')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gray-300 dark:bg-gray-600"></div>
                    <span>{t('tools.bestTimeToCall.timeline.avoid')}</span>
                  </div>
                </div>

                {/* Dual-axis Timeline */}
                <div className="space-y-2">
                  {/* Top axis - Their Time */}
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <Flag countryCode={toConfig?.code} size="lg" />
                      <span className="text-sm font-medium text-muted-foreground">
                        {toCountry && t(`countries.${toCountry}`)} ({t('tools.bestTimeToCall.timeline.theirTime')})
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground px-0.5">
                      {bestHours.hours.filter((_, i) => i % 6 === 0).map((hour) => {
                        // Calculate what their time is at our hour
                        let theirHour = (hour.hour + Math.round(timeDiff || 0)) % 24
                        if (theirHour < 0) theirHour += 24
                        const period = theirHour >= 12 ? 'PM' : 'AM'
                        const displayHour = theirHour === 0 ? 12 : theirHour > 12 ? theirHour - 12 : theirHour
                        return (
                          <span key={`top-${hour.hour}`} className="w-8 text-center">
                            {displayHour} {period}
                          </span>
                        )
                      })}
                    </div>
                  </div>

                  {/* Combined Quality Bar */}
                  <div className="relative py-2">
                    {/* Time marker lines */}
                    <div className="absolute inset-0 flex justify-between px-0.5 pointer-events-none">
                      {[0, 6, 12, 18, 24].map((i) => (
                        <div key={i} className="h-full w-px bg-border/50" style={{ marginLeft: i === 0 ? '0%' : i === 24 ? '0%' : `${(i / 24) * 100}%` }} />
                      ))}
                    </div>
                    {/* Main timeline bar showing combined quality */}
                    <div className="grid grid-cols-24 gap-0.5 h-12 relative">
                      {bestHours.hours.map((hour) => {
                        // Combined quality: both need to be good
                        const combinedQuality: HourQuality =
                          hour.fromQuality === 'avoid' || hour.toQuality === 'avoid' ? 'avoid' :
                          hour.fromQuality === 'okay' || hour.toQuality === 'okay' ? 'okay' : 'great'

                        const theirHour = (hour.hour + Math.round(timeDiff || 0) + 24) % 24
                        const isHovered = hoveredHour === hour.hour

                        return (
                          <div
                            key={`combined-${hour.hour}`}
                            className="relative min-w-0"
                            onMouseEnter={() => setHoveredHour(hour.hour)}
                            onMouseLeave={() => setHoveredHour(null)}
                          >
                            <div
                              className={cn(
                                'h-full rounded-sm cursor-pointer transition-all duration-150',
                                getQualityColor(combinedQuality),
                                combinedQuality === 'great' && 'shadow-sm',
                                isHovered && 'ring-2 ring-primary ring-offset-2 scale-y-110 z-10'
                              )}
                            />

                            {/* Hover popup */}
                            {isHovered && (
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50">
                                <div className="bg-popover text-popover-foreground px-3 py-2 rounded-lg shadow-lg border text-xs whitespace-nowrap">
                                  <div className="font-semibold mb-1">
                                    {hour.hour === new Date().getHours() ? 'Current Hour' : `${formatHourShort(hour.hour)} your time`}
                                  </div>
                                  <div className="space-y-0.5">
                                    <div className="flex items-center gap-2">
<Flag countryCode={fromConfig?.code} size="sm" />
                                       <span>{formatHourShort(hour.hour)}</span>
                                       <span className={cn(
                                         'text-[10px] px-1.5 py-0.5 rounded-full',
                                         hour.fromQuality === 'great' && 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
                                         hour.fromQuality === 'okay' && 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
                                         hour.fromQuality === 'avoid' && 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                       )}>
                                         {hour.fromQuality === 'great' ? 'Great' : hour.fromQuality === 'okay' ? 'Okay' : 'Avoid'}
                                       </span>
                                     </div>
                                     <div className="flex items-center gap-2">
                                      <Flag countryCode={toConfig?.code} size="sm" />
                                       <span>{formatHourShort(theirHour)}</span>
                                      <span className={cn(
                                        'text-[10px] px-1.5 py-0.5 rounded-full',
                                        hour.toQuality === 'great' && 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
                                        hour.toQuality === 'okay' && 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
                                        hour.toQuality === 'avoid' && 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                      )}>
                                        {hour.toQuality === 'great' ? 'Great' : hour.toQuality === 'okay' ? 'Okay' : 'Avoid'}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Arrow */}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-popover" />
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    {/* Current time indicator */}
                    {fromTime && (
                      <div
                        className="absolute top-0 bottom-0 w-0.5 bg-primary z-10"
                        style={{ left: `${((fromTime.hours + fromTime.minutes / 60) / 24) * 100}%` }}
                      >
                        <div className="absolute -top-1 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] px-1 py-0.5 rounded">
                          Now
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bottom axis - Your Time */}
                  <div className="relative">
                    <div className="flex justify-between text-xs text-muted-foreground px-0.5 mb-1">
                      {bestHours.hours.filter((_, i) => i % 6 === 0).map((hour) => {
                        const period = hour.hour >= 12 ? 'PM' : 'AM'
                        const displayHour = hour.hour === 0 ? 12 : hour.hour > 12 ? hour.hour - 12 : hour.hour
                        return (
                          <span key={`bottom-${hour.hour}`} className="w-8 text-center">
                            {displayHour} {period}
                          </span>
                        )
                      })}
                    </div>
                    <div className="flex items-center gap-2">
                      <Flag countryCode={fromConfig?.code} size="lg" />
                      <span className="text-sm font-medium text-muted-foreground">
                        {fromCountry && t(`countries.${fromCountry}`)} ({t('tools.bestTimeToCall.timeline.yourTime')})
                      </span>
                    </div>
                  </div>
                </div>

                {/* Best window highlight */}
                {bestHours.bestStartHour !== null && bestHours.bestEndHour !== null && (
                  <div className="mt-6 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-800 dark:text-green-200">
                        {t('tools.bestTimeToCall.bestTimeTitle')}
                      </span>
                    </div>
                    <p className="text-green-700 dark:text-green-300">
                      {t('tools.bestTimeToCall.recommendation', {
                        start: bestHours.recommendation.split(' - ')[0],
                        end: bestHours.recommendation.split(' - ')[1],
                      })}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CTA */}
            <Card>
              <CardContent className="p-6">
                <Button size="lg" className="w-full" asChild>
                  <a
                    href={`/dialer?country=${toCountry}`}
                    className="flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    {t('tools.bestTimeToCall.cta.button')}
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Popular Routes */}
      <section className="py-12 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold mb-8 text-center">
            {t('tools.bestTimeToCall.popularRoutes.title')}
          </h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {POPULAR_ROUTES.map((route) => {
              const from = COUNTRIES_CONFIG[route.from]
              const to = COUNTRIES_CONFIG[route.to]
              return (
                <Button
                  key={`${route.from}-${route.to}`}
                  variant="outline"
                  className="h-auto py-3 px-4 justify-start"
                  onClick={() => handlePopularRoute(route.from, route.to)}
                >
                    <div className="flex items-center gap-3">
                      <div className="flex -space-x-2">
                        <Flag countryCode={from?.code} size="lg" />
                        <Flag countryCode={to?.code} size="lg" />
                      </div>
                      <span className="text-sm">
                        {t(`countries.${route.from}`)} → {t(`countries.${route.to}`)}
                      </span>
                  </div>
                </Button>
              )
            })}
          </div>
        </div>
      </section>

      <FAQSection
        title={t('tools.bestTimeToCall.faq.title')}
        items={faqItems}
      />

      <CTASection
        translationKeyRoot="tools.bestTimeToCall.cta" ns="tools"
        buttonTo="/dialer"
        buttonIcon={Globe}
      />
    </>
  )
}
